<template>
    <section class="tab-card-section">
        <div class="tab-card-section__container">
            <card-title :module="item" />
            <!-- Tabs -->
            <div v-if="tabs.length > 0" class="tab-card__tabs-wrapper">
                <button v-show="showLeftArrow" class="tab-card__arrow tab-card__arrow--left" type="button"
                    aria-label="scroll left" @click="scrollPrev">
                    <ChevronLeft />
                </button>
                <div ref="tabsRef" class="tab-card__tabs" @scroll="onScroll">
                    <button v-for="(tab, idx) in tabs" :key="idx" class="tab-card__tab"
                        :class="{ 'tab-card__tab--active': activeTab === idx }" @click="activeTab = idx">
                        {{ tab.label }}
                    </button>
                </div>
                <button v-show="showRightArrow" class="tab-card__arrow tab-card__arrow--right" type="button"
                    aria-label="scroll right" @click="scrollNext">
                    <ChevronRight />
                </button>
            </div>
            <div class="tab-card__divider"></div>
            <component :is="resolveComponent(activeTabData.displayModeCode)" :item="activeTabData" :hideTitle="true" />
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref, computed, markRaw, onMounted, onBeforeUnmount, nextTick, watch, type Component } from 'vue'
import type { Module } from '@/api/page_config'
import { CARD_COMPONENT_MAP } from '.';
import CardTitle from './CardTitle.vue';
import ChevronLeft from '@/assets/icons/chevron-left.svg'
import ChevronRight from '@/assets/icons/chevron-right.svg'

const props = defineProps<{
    item: Module
}>()

function resolveComponent(modeCode: string): Component {
    const comp = CARD_COMPONENT_MAP[modeCode]
    return comp ? markRaw(comp) : markRaw(CARD_COMPONENT_MAP.Card)
}


interface TabItem {
    label: string
    item: Module
}

const activeTab = ref(0)
const tabsRef = ref<HTMLDivElement | null>(null)
const showLeftArrow = ref(false)
const showRightArrow = ref(false)

const tabs = computed<TabItem[]>(() => {
    return props.item.items?.map((item) => ({
        label: item.mainTitle,
        item: item
    })) || []
})

const activeTabData = computed(() => {
    return tabs.value[activeTab.value]?.item
})

function updateScrollState(): void {
    const el = tabsRef.value
    if (!el) return
    showLeftArrow.value = el.scrollLeft > 4
    showRightArrow.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
}

function onScroll(): void {
    updateScrollState()
}

function scrollPrev(): void {
    const el = tabsRef.value
    if (!el) return
    const scrollAmount = Math.min(el.clientWidth * 0.8, 200)
    el.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
}

function scrollNext(): void {
    const el = tabsRef.value
    if (!el) return
    const scrollAmount = Math.min(el.clientWidth * 0.8, 200)
    el.scrollBy({ left: scrollAmount, behavior: 'smooth' })
}

let resizeObserver: ResizeObserver | null = null

function initScrollArrows(): void {
    nextTick(() => {
        updateScrollState()
        if (tabsRef.value) {
            resizeObserver = new ResizeObserver(() => {
                updateScrollState()
            })
            resizeObserver.observe(tabsRef.value)
        }
    })
}

onMounted(() => {
    initScrollArrows()
})

onBeforeUnmount(() => {
    resizeObserver?.disconnect()
})

watch(() => props.item.items, () => {
    initScrollArrows()
})

watch(activeTab, async (idx) => {
    await nextTick()
    const el = tabsRef.value
    if (!el) return
    const activeTabEl = el.children[idx] as HTMLElement | null
    if (activeTabEl) {
        const elRect = el.getBoundingClientRect()
        const tabRect = activeTabEl.getBoundingClientRect()
        if (tabRect.left < elRect.left) {
            el.scrollBy({ left: tabRect.left - elRect.left - 16, behavior: 'smooth' })
        } else if (tabRect.right > elRect.right) {
            el.scrollBy({ left: tabRect.right - elRect.right + 16, behavior: 'smooth' })
        }
    }
})

</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.tab-card-section {
    width: 100%;
}

.tab-card-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.tab-card__tabs-wrapper {
    position: relative;
}

.tab-card__tabs {
    display: flex;
    gap: 1.5rem;
    overflow-x: auto;
    overflow-y: hidden;
    touch-action: pan-x;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding: 0 24px;

    &::-webkit-scrollbar {
        display: none;
    }

    @media (min-width: $breakpoint-md) {
        gap: 4rem;
        overflow-x: visible;
        padding: 0;
    }
}

.tab-card__tab {
    padding: 0.5rem 0;
    font-size: 1rem;
    color: $color-text-secondary;
    background: none;
    border: none;
    cursor: pointer;
    position: relative;
    transition: color 0.2s ease;
    white-space: nowrap;
    flex-shrink: 0;

    @media (min-width: $breakpoint-md) {
        font-size: 1.25rem;
        padding: 0.75rem 0;
        flex-shrink: initial;
    }

    &:hover {
        color: $color-text;
    }

    &--active {
        color: $color-text;
        font-weight: 800;

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: $color-text;
            border-radius: 2px;
        }
    }
}

.tab-card__arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #fff;
    color: $color-text;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    padding: 0;


    svg {
        width: 16px;
        height: 16px;
    }

    @media (min-width: $breakpoint-md) {
        width: 35px;
        height: 35px;

        svg {
            width: 24px;
            height: 24px;
        }
    }


    &:active {
        transform: translateY(-50%) scale(0.92);
        background: #f5f5f5;
    }

    &--left {
        left: 0;
    }

    &--right {
        right: 0;
    }
}

.tab-card__divider {
    height: 2px;
    background: #e0e0e0;
    margin-top: 0;
    margin-bottom: 1rem;
}
</style>
